import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import {
  FileText, Check, AlertTriangle, Loader2, ExternalLink, 
  Pen, Shield, CheckCircle, ChevronRight, X
} from 'lucide-react';
import { toast } from 'sonner';
import SignaturePad from './SignaturePad';

/**
 * Contract Signing Modal
 * 
 * This component blocks platform actions until all required contracts are signed.
 * It can be used in two modes:
 * 1. Modal mode: Shows a blocking modal if there are pending contracts
 * 2. Inline mode: Shows contracts inline on a page
 * 
 * Props:
 * - onComplete: Callback when all contracts are signed
 * - mode: 'modal' | 'inline' (default: 'modal')
 * - showOnlyRequired: Only show required contracts (default: true)
 */
const ContractSigningModal = ({ onComplete, mode = 'modal', showOnlyRequired = true, userRole = null }) => {
  const [pendingContracts, setPendingContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContract, setSelectedContract] = useState(null);
  const [signatureName, setSignatureName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [signerTitle, setSignerTitle] = useState('');
  const [signerEmail, setSignerEmail] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signatureImage, setSignatureImage] = useState(null);
  const [showDrawPad, setShowDrawPad] = useState(false);
  const [hasPendingRequired, setHasPendingRequired] = useState(false);
  const [resolvedRole, setResolvedRole] = useState((userRole || '').toLowerCase());
  const [prefillNote, setPrefillNote] = useState('');

  useEffect(() => {
    fetchPendingContracts();
    fetchUserContext();
  }, []);

  // Signal a mandatory agreement gate so lower-priority onboarding popups
  // (welcome video, tours) don't auto-open on top of it.
  useEffect(() => {
    if (mode === 'modal' && hasPendingRequired) {
      window.__agreementGateBlocking = true;
      return () => { window.__agreementGateBlocking = false; };
    }
    window.__agreementGateBlocking = false;
  }, [mode, hasPendingRequired]);

  const fetchUserContext = async () => {
    try {
      // Resolve current role + signer prefill from /auth/me + signature-status
      const me = await api.get('/auth/me').catch(() => null);
      const role = (userRole || me?.data?.role || '').toLowerCase();
      setResolvedRole(role);
      const email = me?.data?.email || '';
      if (email && !signerEmail) setSignerEmail(email);
      // Only client/agency have a Company block — try to prefill it from
      // the most recent acceptance OR their client profile.
      if (role === 'client' || role === 'agency') {
        const sig = await api.get('/contracts/client/signature-status').catch(() => null);
        const latest = sig?.data?.latest;
        if (latest) {
          if (latest.company_name && !companyName) setCompanyName(latest.company_name);
          if (latest.signer_title && !signerTitle) setSignerTitle(latest.signer_title);
          if (latest.signer_email && !signerEmail) setSignerEmail(latest.signer_email);
          if (latest.signature_name && !signatureName) setSignatureName(latest.signature_name);
          if (sig?.data?.needs_resign) {
            const reason = sig.data.reason || 'updates';
            setPrefillNote(`A re-signature is required due to ${reason.replace(/_/g, ' ')}.`);
          }
        } else {
          // Fall back to client_profiles via /client/me
          const cp = await api.get('/client/me').catch(() => null);
          const cname = cp?.data?.company_name || cp?.data?.profile?.company_name;
          if (cname && !companyName) setCompanyName(cname);
        }
      }
    } catch (e) {
      // Non-fatal — user can still fill in fields manually.
    }
  };

  const fetchPendingContracts = async () => {
    try {
      const res = await api.get('/contracts/pending');
      const contracts = showOnlyRequired 
        ? res.data.contracts.filter(c => c.is_required)
        : res.data.contracts;
      setPendingContracts(contracts);
      setHasPendingRequired(res.data.has_pending_required);
    } catch (e) {
      console.error('Failed to fetch contracts:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSignContract = async () => {
    if (!selectedContract) return;
    
    if (!signatureName.trim()) {
      toast.error('Please enter your full legal name');
      return;
    }
    
    if (!agreed) {
      toast.error('You must agree to the terms');
      return;
    }

    // Iter-298 — client + agency must declare company attribution.
    const audience = (selectedContract.audience || '').toLowerCase();
    const requiresCompany = (resolvedRole === 'client' || resolvedRole === 'agency')
      || audience === 'client' || audience === 'both';
    if (requiresCompany) {
      if (!companyName.trim()) {
        toast.error('Company name is required');
        return;
      }
      if (!signerTitle.trim()) {
        toast.error("Signer's title is required (e.g. 'VP, Marketing')");
        return;
      }
      if (!signerEmail.trim() || !/.+@.+\..+/.test(signerEmail.trim())) {
        toast.error("A valid signer email is required");
        return;
      }
    }
    
    setSigning(true);
    try {
      const payload = {
        contract_id: selectedContract.id,
        signature_name: signatureName.trim(),
      };
      if (requiresCompany) {
        payload.company_name = companyName.trim();
        payload.signer_title = signerTitle.trim();
        payload.signer_email = signerEmail.trim();
        // Backend requires an explicit authorized-representative certification
        // for client/agency. The "I agree" checkbox above IS that certification.
        payload.authority_certified = true;
      }
      if (signatureImage) {
        payload.signature_image = signatureImage;
      }
      await api.post('/contracts/accept', payload);
      
      toast.success('Contract signed successfully');
      setSelectedContract(null);
      setSignatureName('');
      setSignatureImage(null);
      // Keep company / signer values so re-sign of a subsequent contract
      // doesn't force re-typing them.
      setAgreed(false);
      
      // Refresh contracts
      await fetchPendingContracts();
      
      // Check if all required are done
      const res = await api.get('/contracts/check-compliance');
      if (res.data.is_compliant && onComplete) {
        onComplete();
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to sign contract');
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return mode === 'modal' ? null : (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-[#03DFFC]" />
      </div>
    );
  }

  // If no pending contracts, don't show anything
  if (pendingContracts.length === 0) {
    return mode === 'inline' ? (
      <div className="text-center py-6 text-green-500">
        <CheckCircle className="w-10 h-10 mx-auto mb-2" />
        <p>All contracts signed!</p>
      </div>
    ) : null;
  }

  const content = (
    <div className="space-y-4" data-testid="contract-signing-modal">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-amber-500/10 rounded-lg">
          <FileText className="w-6 h-6 text-amber-500" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Required Contracts</h2>
          <p className="text-sm text-gray-500">
            Please review and sign the following {pendingContracts.length} contract{pendingContracts.length > 1 ? 's' : ''} to continue
          </p>
        </div>
      </div>

      {/* Warning Banner */}
      {hasPendingRequired && (
        <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-amber-200 font-medium">Action Required</p>
            <p className="text-xs text-amber-200/70">
              You must sign all required contracts before posting gigs, applying for jobs, or receiving payments.
            </p>
          </div>
        </div>
      )}

      {/* Contract List */}
      <div className="space-y-2">
        {pendingContracts.map((contract) => (
          <div
            key={contract.id}
            className={`p-4 rounded-lg border cursor-pointer transition-all ${
              selectedContract?.id === contract.id
                ? 'bg-[#03DFFC]/10 border-[#03DFFC]'
                : 'bg-gray-50 border-gray-200 hover:border-gray-600'
            }`}
            onClick={() => setSelectedContract(contract)}
            data-testid={`contract-item-${contract.id}`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className={`w-5 h-5 ${selectedContract?.id === contract.id ? 'text-[#03DFFC]' : 'text-gray-500'}`} />
                <div>
                  <h3 className="text-gray-900 font-medium">{contract.title}</h3>
                  <p className="text-xs text-gray-500">Version {contract.version}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {contract.is_required && (
                  <Badge className="bg-red-500/20 text-red-500 border-red-500/30">Required</Badge>
                )}
                <ChevronRight className={`w-4 h-4 ${selectedContract?.id === contract.id ? 'text-[#03DFFC]' : 'text-gray-500'}`} />
              </div>
            </div>
            {contract.description && (
              <p className="text-sm text-gray-500 mt-2 ml-8">{contract.description}</p>
            )}
          </div>
        ))}
      </div>

      {/* Selected Contract View & Signing */}
      {selectedContract && (
        <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-gray-900 font-medium">{selectedContract.title}</h3>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const url = selectedContract.pdf_url;
                if (url && !url.includes('example.com')) {
                  const fullUrl = url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
                  window.open(fullUrl, '_blank');
                } else {
                  toast && toast.info('PDF not yet uploaded for this contract');
                }
              }}
              className="border-gray-600 text-gray-600"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              View Full Document
            </Button>
          </div>

          {/* PDF Preview */}
          {selectedContract.pdf_url && (
            <div className="mb-4 bg-white rounded-lg overflow-hidden">
              <iframe
                src={`${selectedContract.pdf_url}#view=FitH`}
                className="w-full h-64"
                title={selectedContract.title}
              />
            </div>
          )}

          {/* Signature Form */}
          <div className="space-y-4 border-t border-gray-200 pt-4 mt-4">
            {((resolvedRole === 'client' || resolvedRole === 'agency') ||
              (selectedContract.audience || '').toLowerCase() === 'client' ||
              (selectedContract.audience || '').toLowerCase() === 'both') && (
              <div className="space-y-3 p-3 rounded-lg bg-amber-50/40 border border-amber-200" data-testid="company-attribution-block">
                <div className="flex items-start gap-2">
                  <Shield className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-900 font-medium">
                    Sign on behalf of your company. iEvntür&apos;s Service Agreement
                    is binding on the company below — not just the individual
                    user. All four fields are required.
                  </p>
                </div>
                {prefillNote && (
                  <p className="text-xs text-rose-700 font-medium" data-testid="resign-reason-banner">{prefillNote}</p>
                )}
                <div className="space-y-2">
                  <label className="text-sm text-gray-700 font-medium">Company name (legal entity)</label>
                  <Input
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="Acme Foods Inc."
                    className="bg-white"
                    data-testid="signer-company-name"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <label className="text-sm text-gray-700 font-medium">Signer title</label>
                    <Input
                      value={signerTitle}
                      onChange={(e) => setSignerTitle(e.target.value)}
                      placeholder="VP, Marketing"
                      className="bg-white"
                      data-testid="signer-title"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm text-gray-700 font-medium">Signer email</label>
                    <Input
                      type="email"
                      value={signerEmail}
                      onChange={(e) => setSignerEmail(e.target.value)}
                      placeholder="jane@acme.com"
                      className="bg-white"
                      data-testid="signer-email"
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <label className="text-sm text-gray-500 flex items-center gap-2">
                <Pen className="w-4 h-4" />
                Type your full legal name as signature
              </label>
              <Input
                value={signatureName}
                onChange={(e) => setSignatureName(e.target.value)}
                placeholder="John Smith"
                className="bg-white border-gray-200 text-gray-900 font-medium"
                style={{ fontFamily: 'cursive' }}
                data-testid="signature-input"
              />
              <div className="flex items-center gap-3 pt-1">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDrawPad(true)}
                  className="border-gray-300 text-gray-700"
                  data-testid="draw-signature-btn"
                >
                  <Pen className="w-4 h-4 mr-2" />
                  {signatureImage ? 'Redraw signature' : 'Draw signature instead'}
                </Button>
                {signatureImage && (
                  <span className="flex items-center gap-2" data-testid="drawn-signature-preview">
                    <img src={signatureImage} alt="signature" className="h-10 border border-gray-200 rounded bg-white" />
                    <button type="button" onClick={() => setSignatureImage(null)} className="text-xs text-rose-600 underline">Clear</button>
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Checkbox
                id="agree-terms"
                checked={agreed}
                onCheckedChange={setAgreed}
                className="border-gray-600 data-[state=checked]:bg-[#03DFFC]"
              />
              <label htmlFor="agree-terms" className="text-sm text-gray-600 cursor-pointer">
                I have read and agree to the terms of this contract. I understand that this electronic signature is legally binding.
              </label>
            </div>

            <div className="flex items-center gap-3">
              <Button
                onClick={handleSignContract}
                disabled={signing || !signatureName.trim() || !agreed}
                className="flex-1 bg-[#03DFFC] hover:bg-[#03DFFC] text-gray-900"
                data-testid="sign-contract-btn"
              >
                {signing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Signing...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Sign Contract
                  </>
                )}
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setSelectedContract(null);
                  setSignatureName('');
                  setSignatureImage(null);
                  setAgreed(false);
                }}
                className="text-gray-500 hover:text-gray-900"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      <SignaturePad
        open={showDrawPad}
        onClose={() => setShowDrawPad(false)}
        title="Draw your signature"
        description="Draw your signature below to sign this agreement."
        confirmLabel="Use this signature"
        onCapture={(img) => { setSignatureImage(img); setShowDrawPad(false); }}
      />
    </div>
  );

  if (mode === 'inline') {
    return content;
  }

  // Modal Mode
  return (
    <div role="dialog" aria-modal="true" className="fixed inset-0 z-[90] flex items-center justify-center bg-gray-50/90 p-4">
      <Card className="bg-white border-gray-200 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardContent className="p-6">
          {content}
        </CardContent>
      </Card>
    </div>
  );
};

/**
 * Hook to check contract compliance
 * Returns { isCompliant, loading, checkCompliance, pendingCount }
 */
export const useContractCompliance = () => {
  const [isCompliant, setIsCompliant] = useState(true);
  const [loading, setLoading] = useState(true);
  const [pendingCount, setPendingCount] = useState(0);

  const checkCompliance = async () => {
    try {
      const res = await api.get('/contracts/check-compliance');
      setIsCompliant(res.data.is_compliant);
      setPendingCount(res.data.pending_required_contracts || 0);
      return res.data.is_compliant;
    } catch (e) {
      console.error('Failed to check compliance:', e);
      return true; // Fail open
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkCompliance();
  }, []);

  return { isCompliant, loading, checkCompliance, pendingCount };
};

/**
 * Higher-order component to wrap pages that require contract compliance
 */
export const withContractGate = (WrappedComponent) => {
  return function ContractGatedComponent(props) {
    const { isCompliant, loading, checkCompliance } = useContractCompliance();
    const [showModal, setShowModal] = useState(false);

    useEffect(() => {
      if (!loading && !isCompliant) {
        setShowModal(true);
      }
    }, [loading, isCompliant]);

    const handleComplete = async () => {
      const compliant = await checkCompliance();
      if (compliant) {
        setShowModal(false);
      }
    };

    if (loading) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#03DFFC]" />
        </div>
      );
    }

    return (
      <>
        {showModal && (
          <ContractSigningModal onComplete={handleComplete} mode="modal" />
        )}
        <WrappedComponent {...props} />
      </>
    );
  };
};

export default ContractSigningModal;
